import React, { useState, useEffect } from 'react';
import api, { facilityAPI } from '../services/api';
import { TrendingDown, CheckCircle, DollarSign, Leaf, RefreshCw, ChevronDown, ChevronUp, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

const PAYBACK_COLOR = (years) => {
  if (years <= 2) return { bg: '#F0FDF4', color: '#16A34A', label: 'Excellent ROI' };
  if (years <= 5) return { bg: '#EFF6FF', color: '#0066FF', label: 'Good ROI' };
  if (years <= 10) return { bg: '#FFFBEB', color: '#D97706', label: 'Moderate ROI' };
  return { bg: '#FEF2F2', color: '#DC2626', label: 'Long-term' };
};

const EFFORT_COLOR = (effort) => {
  if (effort === 'Low') return { bg: '#F0FDF4', color: '#16A34A' };
  if (effort === 'Medium') return { bg: '#FFFBEB', color: '#D97706' };
  return { bg: '#FEF2F2', color: '#DC2626' };
};

function GHGReductions() {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [ghgSummary, setGhgSummary] = useState(null);
  const [strategiesByFacility, setStrategiesByFacility] = useState({});
  const [adoptedByFacility, setAdoptedByFacility] = useState({});
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('All');
  const year = new Date().getFullYear();

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    // Reset filter when facility changes
    setFilterCat('All');
  }, [selectedFacility]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [facRes, sumRes] = await Promise.all([
        facilityAPI.getAll(),
        api.get(`/ghg/summary?year=${year}`),
      ]);
      setFacilities(facRes.data.facilities || []);
      setGhgSummary(sumRes.data);

      // Load saved strategies from DB
      const savedRes = await api.get(`/ghg/strategies?year=${new Date().getFullYear()}`);
      const rows = savedRes.data.strategies || [];
      const strategiesMap = {};
      const adoptedMap = {};
      rows.forEach(row => {
        if (row.facility_id) {
          strategiesMap[row.facility_id] = row.strategy_data;
          adoptedMap[row.facility_id] = row.adopted || [];
        }
      });
      setStrategiesByFacility(strategiesMap);
      setAdoptedByFacility(adoptedMap);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveToStorage = async (strategies, adopted, facilityId) => {
    try {
      await api.post('/ghg/strategies/save', {
        facility_id: parseInt(facilityId),
        strategy_data: strategies[facilityId] || [],
        adopted: adopted[facilityId] || [],
        year: new Date().getFullYear(),
      });
    } catch (e) { console.error('Failed to save strategies', e); }
  };

  const generateStrategies = async () => {
    if (!selectedFacility) return;
    try {
      setGenerating(true);
      const res = await api.post('/ghg/reduction-strategies', {
        facility_id: parseInt(selectedFacility),
        year,
      });
      const parsed = res.data.strategies;
      const updated = { ...strategiesByFacility, [selectedFacility]: parsed };
      setStrategiesByFacility(updated);
      saveToStorage(updated, adoptedByFacility, selectedFacility);
      toast.success(`Generated ${parsed.length} strategies`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate strategies');
    } finally {
      setGenerating(false);
    }
  };

  const toggleAdopt = async (strategyId) => {
    const current = adoptedByFacility[selectedFacility] || [];
    const updated = current.includes(strategyId)
      ? current.filter(id => id !== strategyId)
      : [...current, strategyId];
    const newAdopted = { ...adoptedByFacility, [selectedFacility]: updated };
    setAdoptedByFacility(newAdopted);
    try {
      await api.put('/ghg/strategies/adopted', {
        facility_id: parseInt(selectedFacility),
        adopted: updated,
        year: new Date().getFullYear(),
      });
    } catch (e) { console.error('Failed to save adopted', e); }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // Current facility data
  const currentStrategies = selectedFacility ? (strategiesByFacility[selectedFacility] || []) : [];
  const currentAdopted = selectedFacility ? (adoptedByFacility[selectedFacility] || []) : [];
  const adoptedStrategies = currentStrategies.filter(s => currentAdopted.includes(s.id));
  const totalReduction = adoptedStrategies.reduce((sum, s) => sum + (s.co2e_reduction_mt || 0), 0);
  const totalSavings = adoptedStrategies.reduce((sum, s) => sum + (s.annual_savings_usd || 0), 0);
  const totalCost = adoptedStrategies.reduce((sum, s) => sum + ((s.implementation_cost_low + s.implementation_cost_high) / 2 || 0), 0);

  const CATEGORIES = ['All', ...new Set(currentStrategies.map(s => s.category))];
  const filtered = currentStrategies.filter(s => filterCat === 'All' || s.category === filterCat);

  // All facilities summary
  const allFacilitiesHaveStrategies = facilities.filter(f => strategiesByFacility[f.id]);
  const allTotalReduction = Object.values(adoptedByFacility).flat().reduce((sum, id) => {
    for (const fid of Object.keys(strategiesByFacility)) {
      const s = strategiesByFacility[fid]?.find(s => s.id === id);
      if (s) return sum + (s.co2e_reduction_mt || 0);
    }
    return sum;
  }, 0);

  const selectedFacilityName = facilities.find(f => f.id === parseInt(selectedFacility))?.facility_name || '';

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: '#0A1628', margin: '0 0 4px' }}>Reduction Strategies</h1>
          <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>AI-powered GHG reduction recommendations</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedFacility} onChange={e => setSelectedFacility(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14, minWidth: 180 }}>
            <option value="">All Facilities (Overview)</option>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name}</option>)}
          </select>
          {selectedFacility ? (
            <button onClick={generateStrategies} disabled={generating} className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <RefreshCw size={14} style={{ animation: generating ? 'spin 1s linear infinite' : 'none' }} />
              {generating ? 'Analyzing...' : currentStrategies.length ? 'Regenerate' : 'Analyze Facility'}
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', color: '#D97706', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500 }}>
              <AlertTriangle size={14} /> Select a facility to analyze
            </div>
          )}
        </div>
      </div>

      {/* All facilities overview */}
      {!selectedFacility && (
        <div>
          {/* Portfolio summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
            {[
              { label: 'Total Portfolio CO2e', value: `${parseFloat(ghgSummary?.total_co2e || 0).toFixed(1)} mt`, color: '#0066FF', bg: '#EFF6FF' },
              { label: 'Facilities Analyzed', value: `${allFacilitiesHaveStrategies.length} of ${facilities.length}`, color: '#16A34A', bg: '#F0FDF4' },
              { label: 'Total Adopted Reduction', value: `${allTotalReduction.toFixed(1)} mt CO2e/yr`, color: '#D97706', bg: '#FFFBEB' },
            ].map((s, i) => (
              <div key={i} className="card" style={{ padding: '16px 20px', background: s.bg, border: 'none' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: 'var(--font-heading)' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Per facility status */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <div className="section-label" style={{ marginBottom: 14 }}>Facility Analysis Status</div>
            {facilities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontFamily: 'var(--font-body)', fontSize: 14 }}>No facilities found</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {facilities.map(f => {
                  const hasStrategies = !!strategiesByFacility[f.id];
                  const adopted = (adoptedByFacility[f.id] || []).length;
                  const strategies = (strategiesByFacility[f.id] || []).length;
                  const facilityEmissions = ghgSummary?.by_facility?.find(x => x.facility_name === f.facility_name);
                  return (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#FAFAFA', borderRadius: 8, border: '1px solid #F1F5F9' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: hasStrategies ? '#16A34A' : '#CBD5E1', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{f.facility_name}</div>
                        <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)' }}>
                          {facilityEmissions ? `${parseFloat(facilityEmissions.total_co2e).toFixed(1)} mt CO2e` : 'No emissions logged'}
                          {hasStrategies ? ` · ${strategies} strategies · ${adopted} adopted` : ' · Not yet analyzed'}
                        </div>
                      </div>
                      <button onClick={() => setSelectedFacility(String(f.id))}
                        style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #0066FF', background: hasStrategies ? '#EFF6FF' : 'white', color: '#0066FF', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {hasStrategies ? 'View Strategies' : 'Analyze'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Facility strategies */}
      {selectedFacility && (
        <div>
          {/* Back link */}
          <button onClick={() => setSelectedFacility('')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#0066FF', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, marginBottom: 16, padding: 0 }}>
            ← All Facilities
          </button>

          {/* Adopted summary */}
          {adoptedStrategies.length > 0 && (
            <div style={{ padding: '16px 20px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, marginBottom: 20 }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#15803D', marginBottom: 10 }}>
                ✅ {selectedFacilityName} — Adopted Plan ({adoptedStrategies.length} strategies)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {[
                  { label: 'CO2e Reduction', value: `${totalReduction.toFixed(1)} mt/yr` },
                  { label: 'Annual Savings', value: `$${totalSavings.toLocaleString()}` },
                  { label: 'Investment', value: `$${Math.round(totalCost).toLocaleString()}` },
                ].map((s, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 11, color: '#16A34A', fontFamily: 'var(--font-body)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#15803D', fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {currentStrategies.length === 0 && (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <TrendingDown size={48} color="#CBD5E1" style={{ margin: '0 auto 16px', display: 'block' }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', marginBottom: 8 }}>No strategies yet for {selectedFacilityName}</div>
              <div style={{ fontSize: 14, color: '#94a3b8', fontFamily: 'var(--font-body)', marginBottom: 24 }}>Click "Analyze Facility" to generate AI-powered reduction strategies</div>
              <button onClick={generateStrategies} disabled={generating} className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} style={{ animation: generating ? 'spin 1s linear infinite' : 'none' }} />
                {generating ? 'Analyzing...' : 'Analyze Facility'}
              </button>
            </div>
          )}

          {/* Category filters */}
          {currentStrategies.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setFilterCat(cat)}
                  style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${filterCat === cat ? '#0066FF' : '#E2E8F0'}`, background: filterCat === cat ? '#EFF6FF' : 'white', color: filterCat === cat ? '#0066FF' : '#374151', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: filterCat === cat ? 600 : 400, cursor: 'pointer' }}>
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Strategy cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map((strategy, i) => {
              const isAdopted = currentAdopted.includes(strategy.id);
              const isExpanded = expanded[strategy.id];
              const payback = PAYBACK_COLOR(strategy.payback_years);
              const effort = EFFORT_COLOR(strategy.implementation_effort);

              return (
                <div key={strategy.id} className="card" style={{ padding: 0, overflow: 'hidden', border: isAdopted ? '2px solid #16A34A' : '1px solid #E2E8F0' }}>
                  <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: '#F8FAFC', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 13, color: '#6B7280' }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628' }}>{strategy.title}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#F1F5F9', color: '#64748b' }}>{strategy.category}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#EFF6FF', color: '#0066FF' }}>Scope {strategy.scope}</span>
                        {isAdopted && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#F0FDF4', color: '#16A34A' }}>✓ Adopted</span>}
                      </div>
                      <div style={{ fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)', lineHeight: 1.6, marginBottom: 10 }}>{strategy.description}</div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Leaf size={12} color="#16A34A" />
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#16A34A', fontFamily: 'var(--font-body)' }}>{strategy.co2e_reduction_mt} mt CO2e/yr ({strategy.co2e_reduction_pct}%)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <DollarSign size={12} color="#6B7280" />
                          <span style={{ fontSize: 12, color: '#374151', fontFamily: 'var(--font-body)' }}>${strategy.implementation_cost_low?.toLocaleString()}–${strategy.implementation_cost_high?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <DollarSign size={12} color="#16A34A" />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#16A34A', fontFamily: 'var(--font-body)' }}>${strategy.annual_savings_usd?.toLocaleString()}/yr</span>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: payback.bg, color: payback.color }}>{strategy.payback_years}yr · {payback.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: effort.bg, color: effort.color }}>{strategy.implementation_effort} effort</span>
                        <span style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--font-body)' }}>⏱ {strategy.timeline_months}mo</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                      <button onClick={() => toggleAdopt(strategy.id)}
                        style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${isAdopted ? '#16A34A' : '#E2E8F0'}`, background: isAdopted ? '#F0FDF4' : 'white', color: isAdopted ? '#16A34A' : '#374151', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                        {isAdopted ? <><CheckCircle size={12} /> Adopted</> : <><Plus size={12} /> Adopt</>}
                      </button>
                      <button onClick={() => toggleExpand(strategy.id)}
                        style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #E2E8F0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {isExpanded ? <ChevronUp size={13} color="#6B7280" /> : <ChevronDown size={13} color="#6B7280" />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #F1F5F9', padding: '16px 20px', background: '#FAFAFA' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Implementation Steps</div>
                          <div style={{ fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{strategy.how_to_implement}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Details</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {strategy.applicable_standards && (
                              <div style={{ padding: '8px 12px', background: '#EFF6FF', borderRadius: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#0066FF', marginBottom: 2 }}>Standards & Incentives</div>
                                <div style={{ fontSize: 12, color: '#374151', fontFamily: 'var(--font-body)' }}>{strategy.applicable_standards}</div>
                              </div>
                            )}
                            {strategy.co_benefits && (
                              <div style={{ padding: '8px 12px', background: '#F0FDF4', borderRadius: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#16A34A', marginBottom: 2 }}>Co-Benefits</div>
                                <div style={{ fontSize: 12, color: '#374151', fontFamily: 'var(--font-body)' }}>{strategy.co_benefits}</div>
                              </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                              {[
                                { label: 'CO2e Reduction', value: `${strategy.co2e_reduction_mt} mt/yr` },
                                { label: '% of Emissions', value: `${strategy.co2e_reduction_pct}%` },
                                { label: 'Cost Range', value: `$${strategy.implementation_cost_low?.toLocaleString()}–$${strategy.implementation_cost_high?.toLocaleString()}` },
                                { label: 'Annual Savings', value: `$${strategy.annual_savings_usd?.toLocaleString()}` },
                                { label: 'Payback', value: `${strategy.payback_years} years` },
                                { label: 'Timeline', value: `${strategy.timeline_months} months` },
                              ].map((item, i) => (
                                <div key={i} style={{ padding: '7px 10px', background: 'white', borderRadius: 8, border: '1px solid #F1F5F9' }}>
                                  <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{item.label}</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{item.value}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default GHGReductions;
